import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as Sentry from '@sentry/react';

import { usePrototypePreview } from 'Pages/TeamDashboard/CreateReview/components/PrototypePreviewContext';

import { setCurrentRecord } from 'Redux/Actions/historyActions';

import { getSingleDesignReview } from 'services/teamDashboardReviews';

import { CHAT_EVENT_TYPES } from 'utlis/chatPrototype';
import { DESIGN_REMIX_STATUS } from 'utlis/constants';
import { getInsightRemixPreview, getRemixProcessState } from 'utlis/remixVariants';

const GENERIC_ERROR = 'Something went wrong while starting the prototype. Please try again.';
const FAILED_ERROR = 'Prototype generation failed. Please try again.';
const NOT_SYNCED_ERROR = 'The prototype was started, but its progress could not be loaded. Please refresh the page.';

/**
 * Prototypes and variants started from the Ask Audit chat. The chat does not poll: it refreshes the review so the New Idea
 * card panel finds the running process and polls it. The chat only tracks the process, to show "Generating..."
 * and keep the input disabled until it completes or fails.
 */
export function useChatPrototypeJobs({ feedback, onSelectInsight, onChatJobCompleted }) {
  const dispatch = useDispatch();
  const store = useStore();
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);
  const { markChatProcess } = usePrototypePreview();

  const [activeJobs, setActiveJobs] = useState([]);
  const [notice, setNotice] = useState('');
  const [refinementChooser, setRefinementChooser] = useState(null);

  // Jobs whose chat messages are being loaded after they completed
  const completingJobsRef = useRef(new Set());

  const getJobState = useCallback((job, record) => getRemixProcessState(
    getInsightRemixPreview(record, job.insightUuid),
    job.processUuid
  ), []);

  const removeJob = useCallback(processUuid => {
    setActiveJobs(jobs => jobs.filter(job => job.processUuid !== processUuid));
  }, []);

  const refreshRecord = useCallback(async () => {
    const reviewUuid = store.getState().historyReducer.currentRecord?.uuid;

    if (!reviewUuid) {
      return;
    }

    const review = await getSingleDesignReview(reviewUuid);

    // The user may have opened another audit in the meantime
    if (review?.uuid === reviewUuid && store.getState().historyReducer.currentRecord?.uuid === reviewUuid) {
      dispatch(setCurrentRecord(review));
    }
  }, [dispatch, store]);

  const handlePrototypeEvent = useCallback(async event => {
    if (event.type === CHAT_EVENT_TYPES.PROTOTYPE_ERROR) {
      setNotice(event.message || GENERIC_ERROR);

      return;
    }

    const isRefinementOptions = event.type === CHAT_EVENT_TYPES.REFINEMENT_OPTIONS;

    if (
      !isRefinementOptions
      && event.type !== CHAT_EVENT_TYPES.PROTOTYPE_JOB_STARTED
      && event.type !== CHAT_EVENT_TYPES.PROTOTYPE_JOB_STATUS
    ) {
      return;
    }

    const insight = feedback.find(item => String(item.uuid) === String(event.insight_uuid));

    // The options are for a variant of a new idea of this audit
    if (isRefinementOptions) {
      if (insight) {
        setRefinementChooser({
          options: (Array.isArray(event.options) ? event.options : [])
            .filter(option => option?.id && typeof option.label === 'string' && option.label.trim()),
          customInputLabel: event.custom_input?.label,
        });
      }
      else {
        Sentry.captureException(new Error('Chat refinement options do not match a new idea of the audit'), {
          extra: { event },
        });
      }

      return;
    }

    // Without its card nothing would show or poll the prototype
    if (!event.process_uuid || !insight) {
      Sentry.captureException(new Error('Chat prototype event does not match a new idea of the audit'), {
        extra: { event },
      });
      setNotice(GENERIC_ERROR);

      return;
    }

    const isStartedFromChat = event.type === CHAT_EVENT_TYPES.PROTOTYPE_JOB_STARTED;
    const job = {
      processUuid: event.process_uuid,
      insightUuid: String(event.insight_uuid),
      operation: event.operation,
      isStartedFromChat,
    };

    setNotice('');
    setRefinementChooser(null);

    if (isStartedFromChat) {
      markChatProcess(job.processUuid);
    }

    // The same process can be announced again while it is running: track it once
    setActiveJobs(jobs => (jobs.some(item => item.processUuid === job.processUuid) ? jobs : [...jobs, job]));
    // Selects the card and its screen, so the generation shows next to the original design
    onSelectInsight(insight);

    try {
      await refreshRecord();
    }
    catch (error) {
      Sentry.captureException(error);
    }

    // Not in the review: the card cannot poll it, so do not keep the chat disabled
    if (!getJobState(job, store.getState().historyReducer.currentRecord)) {
      removeJob(job.processUuid);
      setNotice(NOT_SYNCED_ERROR);
    }
  }, [feedback, getJobState, markChatProcess, onSelectInsight, refreshRecord, removeJob, store]);

  // The stream broke: a prototype may have been started that the chat never heard about
  const handleStreamInterrupted = useCallback(() => {
    refreshRecord().catch(() => {});
  }, [refreshRecord]);

  const clearNotice = useCallback(() => setNotice(''), []);

  const closeRefinementChooser = useCallback(() => setRefinementChooser(null), []);

  // The card panel saves the result into the review: enable the chat again once the process ends
  useEffect(() => {
    activeJobs.forEach(job => {
      const state = getJobState(job, currentRecord);

      if (state?.status === DESIGN_REMIX_STATUS.COMPLETED) {
        if (completingJobsRef.current.has(job.processUuid)) {
          return;
        }

        completingJobsRef.current.add(job.processUuid);

        // The backend updates the chat message of a job started from the chat
        const loadMessages = job.isStartedFromChat && onChatJobCompleted
          ? onChatJobCompleted().catch(() => {
            // the message is then updated on the next reload
          })
          : Promise.resolve();

        loadMessages.then(() => {
          completingJobsRef.current.delete(job.processUuid);
          removeJob(job.processUuid);
        });
      }
      else if (state?.status === DESIGN_REMIX_STATUS.FAILED) {
        removeJob(job.processUuid);
        setNotice(state.errorMessage || FAILED_ERROR);
      }
    });
  }, [activeJobs, currentRecord, getJobState, onChatJobCompleted, removeJob]);

  return {
    activeJobs,
    notice,
    clearNotice,
    refinementChooser,
    closeRefinementChooser,
    handlePrototypeEvent,
    handleStreamInterrupted,
  };
}
